import { load } from 'cheerio'
import { escapeHtml } from '@messenger/utils'
import type { CheerioAPI } from 'cheerio'
import type { BasicAcceptedElems } from 'cheerio'
import type { AnyNode } from 'domhandler'

const visibleText = (text: string): string =>
  text.replace(/[\s\u200a\u200b]+/g, '')

/** Whether every paragraph of `$element` with text has all of it inside `tag`. */
const wrapsAllText = (
  $: CheerioAPI,
  $element: ReturnType<CheerioAPI>,
  tag: string,
): boolean => {
  const paragraphs = $element.find('p').length
    ? $element.find('p').toArray()
    : [$element.get(0)]
  const withText = paragraphs.filter(
    (paragraph) => paragraph && visibleText($(paragraph).text()) !== '',
  )
  return (
    withText.length > 0 &&
    withText.every((paragraph) => {
      const $paragraph = $(paragraph)
      const inside = $paragraph
        .find(tag)
        .toArray()
        .filter((node) => $(node).parents(tag).length === 0)
        .map((node) => $(node).text())
        .join('')
      return visibleText(inside) === visibleText($paragraph.text())
    })
  )
}

export const preserveItalicsAndBolds = ({
  initialElement,
  targetElement,
  targetTag,
  findLast = true,
}: {
  initialElement: BasicAcceptedElems<AnyNode>
  targetElement: CheerioAPI
  targetTag: string
  findLast?: boolean
}) => {
  if (!initialElement || !targetElement || !targetTag) return
  const $ = load('')
  const $element = $(initialElement)
  // A block (findLast false) is restored bold or italic only when the whole
  // block was: every paragraph's text inside the tag. A block holding the
  // rich-text field's own bold word keeps its words as the new value has them.
  const formats = (tag: string): boolean =>
    findLast ? $element.find(tag).length > 0 : wrapsAllText($, $element, tag)
  const hasEm = formats('em')
  const hasStrong = formats('strong')

  if (!hasEm && !hasStrong) return

  targetElement(targetTag).each((_, element) => {
    const loadedElement = targetElement(element)
    let spanText
    let lastElement

    if (!findLast) {
      spanText = loadedElement.text()
      lastElement = loadedElement
    } else {
      const lastSpan = loadedElement.find('span').last()
      const lastParagraph = loadedElement.find('p').last()
      lastElement = lastSpan.length > 0 ? lastSpan : lastParagraph
      spanText = lastElement.text()
    }

    // `.text()` decodes entities, so the text goes back escaped: a value that
    // spells a tag stays text instead of becoming that tag.
    let newContent = escapeHtml(spanText)

    if (hasEm) newContent = `<em>${newContent}</em>`
    if (hasStrong) newContent = `<strong>${newContent}</strong>`

    lastElement.html(newContent)
  })
}

export default preserveItalicsAndBolds
